<template>
  <SoDialog
    ref="dialog"
    v-model="value"
    card-title="Update Status"
    data-testid="reading-update-modal"
  >
    <section>
      <SoForm
        v-model="isValid"
        data-testid="reading-update-form"
        @submit="handleSubmit()"
      >
        <div>
          <label data-testid="reading-update-status-label">
            Status
            <SoSelect
              v-model="selectedStatus"
              data-testid="reading-update-status"
              class="mt-2"
              :items="statusItems"
              :rules="[rules.required]"
              placeholder="Select Status"
            />
          </label>

          <SoTextarea
            v-model="comment"
            data-testid="reading-update-comment"
            class="mt-2"
            label="Comment"
            :rules="
              selectedStatus === 'NO_ACTION_REQUIRED' ? [rules.required] : []
            "
          />
        </div>
        <div class="d-flex gap-2 mt-2">
          <SoButton
            :loading="updateStatusAndComment.isPending"
            title="Confirm"
            type="success"
            data-testid="reading-update-confirm"
            submit
            large
          />
          <SoButton
            data-testid="reading-update-cancel"
            title="Cancel"
            type="secondary"
            large
            @click="value = false"
          />
        </div>
      </SoForm>
    </section>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { useSnackbarStore } from "@/stores/snackbar"
  import SoTextarea from "@/components/formControls/SoTextarea.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import rules from "@/validators/rules"
  import { IMeterReading } from "@/models/operations/meterReading/meterReading.types"
  import { MeterReadingsMutator } from "@/mutators/meterReadings/MeterReadingsMutator"
  import { errorReporter } from "@/services/ErrorService"

  export default defineComponent({
    components: {
      SoButton,
      SoDialog,
      SoTextarea,
      SoSelect,
      SoForm,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
      currentRow: {
        type: Object as PropType<IMeterReading>,
        default: null,
        required: true,
      },
    },
    emits: ["update:modelValue", "submitted"],
    data() {
      return {
        snackbar: useSnackbarStore(),
        comment: "",
        selectedStatus: undefined,
        statusItems: [
          { title: "Manual Submission", value: "MANUAL_SUBMISSION" },
          { title: "Contacted", value: "CONTACTED" },
          { title: "No action required", value: "NO_ACTION_REQUIRED" },
        ],
        isValid: false,
        rules: rules,
        updateStatusAndComment: MeterReadingsMutator.updateStatusAndComment(),
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
    },
    watch: {
      currentRow: {
        handler: function (rowData) {
          this.comment = rowData.comment ?? ""
          this.selectedStatus = undefined
        },
        immediate: true,
      },
    },
    methods: {
      async handleSubmit() {
        try {
          const submissionId = this.currentRow.id
          await this.updateStatusAndComment.mutateAsync({
            submissionId,
            status: this.selectedStatus,
            comment: this.comment,
          })
          this.value = false
          this.snackbar.addSuccess("Status successfully updated")
          this.$emit("submitted")
        } catch (error) {
          console.error("Error updating status:", error)
          errorReporter.report(error)
          this.snackbar.addError(
            "There was an error updating the status. Please try again.",
          )
        }
      },
    },
  })
</script>
